import { PhotoModel } from '../types/protocols';

/**
 * App-wide photo order: newest photo first, everywhere (customer wish). When a
 * set spans more than one year it is shown under year headings, newest year first.
 */

/** Anything orderable as a photo: its push key and stored `YYYY-MM-DD hh:mm:ss` date. */
export interface DatedItem {
    key: string;
    date?: string;
}

export interface YearGroup<T> {
    /** The year, or null when every item falls in the same year (no heading is shown). */
    year: string | null;
    items: T[];
}

export const UNKNOWN_YEAR = 'Okänt år';

/** Year of a stored `YYYY-MM-DD hh:mm:ss` date, or null when the date is missing or malformed. */
export const yearOf = (date: string | undefined): string | null => {
    const match = /^(\d{4})-/.exec(date ?? '');
    return match ? match[1] : null;
};

/**
 * Newest first. Stored dates order correctly as strings; equal or missing dates
 * fall back to the push key (newest first), so undated items go last.
 */
export const compareNewestFirst = (a: DatedItem, b: DatedItem): number => {
    const dateA = a.date ?? '';
    const dateB = b.date ?? '';
    if (dateA !== dateB) return dateA < dateB ? 1 : -1;
    return a.key < b.key ? 1 : a.key > b.key ? -1 : 0;
};

/** The photos of a map, newest first, each carrying its map key as `key`. */
export const orderPhotos = (photos: { [key: string]: PhotoModel }): PhotoModel[] =>
    Object.entries(photos)
        .map(([key, photo]) => ({ ...photo, key }))
        .sort(compareNewestFirst);

/**
 * Split items that are already newest first into year groups. When all items
 * share one year the result is a single group with `year: null`, so callers show
 * headings only when the items span more than one year. Undated items come last,
 * under "Okänt år".
 */
export const groupByYear = <T extends DatedItem>(ordered: T[]): YearGroup<T>[] => {
    const groups: YearGroup<T>[] = [];
    for (const item of ordered) {
        const year = yearOf(item.date) ?? UNKNOWN_YEAR;
        const last = groups[groups.length - 1];
        if (last && last.year === year) {
            last.items.push(item);
        } else {
            groups.push({ year, items: [item] });
        }
    }
    return groups.length <= 1 ? [{ year: null, items: ordered }] : groups;
};
